import { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Card,
	Col,
	DatePicker,
	Empty,
	InputNumber,
	Row,
	Select,
	Space,
	Statistic,
	Table,
	Tag,
	Tooltip,
	Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import {
	Bar,
	BarChart,
	CartesianGrid,
	Legend,
	ResponsiveContainer,
	Tooltip as ChartTooltip,
	XAxis,
	YAxis,
} from 'recharts';

import Header from '../../components/Header';
import {
	KittingSessionRow,
	KittingSessionStatus,
	useGetKittingLogQuery,
	useGetKittingSummaryQuery,
} from '../../redux/api/kitting-report';

const { Text } = Typography;
const { RangePicker } = DatePicker;

/** Matches the backend default; Operations works in the 30-45 minute range. */
const DEFAULT_TIMEOUT_MINUTES = 45;

/**
 * The session list is capped server-side when no job or account filter
 * narrows it, so past this the page shows the summary alone.
 */
const MAX_SESSION_LIST_DAYS = 7;

/** Wide enough for a first and last name without crowding the filter row. */
const ACCOUNT_SELECT_WIDTH = 260;

/**
 * How long the threshold input settles before the reports are re-queried.
 * Every value is a separate cache key on two endpoints, and typing "120"
 * passes through 1 and 12 on the way -- neither of which anybody asked for.
 */
const TIMEOUT_SETTLE_MS = 500;

/**
 * Kitting tags are printed from a shared tablet screen that, unlike the
 * cutting and delivery screens beside it, never asks who is standing there.
 * So the only identity on a print is whichever account that tablet is signed
 * in to -- useful for telling stations apart, useless for judging people.
 */
const ACCOUNT_PROVENANCE_NOTE =
	'The account signed in to the station that printed the tags. The kitting ' +
	'screen does not ask which operator is using it, so treat this as which ' +
	'station did the work, not who.';

const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

const STATUS_LABELS: Record<KittingSessionStatus, string> = {
	completed: 'Completed',
	timeout: 'Timed out',
	in_progress: 'In progress',
};

const STATUS_COLORS: Record<KittingSessionStatus, string> = {
	completed: 'green',
	timeout: 'red',
	in_progress: 'blue',
};

const formatDuration = (seconds: number | null | undefined) => {
	if (seconds === null || seconds === undefined) return '—';
	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	if (hours >= 24) {
		const days = Math.floor(hours / 24);
		return `${days}d ${hours % 24}h`;
	}
	if (hours) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
	return `${minutes}m ${String(seconds % 60).padStart(2, '0')}s`;
};

const formatTime = (iso: string | null) =>
	iso ? dayjs(iso).format('MMM D, HH:mm') : '—';

interface AttemptBreakdownRow {
	key: string;
	attempt: string;
	completed: number;
	timeouts: number;
	average: number | null;
}

const KittingTimes = () => {
	const [range, setRange] = useState<[Dayjs, Dayjs]>([
		dayjs().subtract(6, 'day'),
		dayjs(),
	]);
	// What the input shows versus what the reports were asked for; they meet
	// once the operator stops typing.
	const [timeoutDraft, setTimeoutDraft] = useState<number>(
		DEFAULT_TIMEOUT_MINUTES
	);
	const [timeoutMinutes, setTimeoutMinutes] = useState<number>(
		DEFAULT_TIMEOUT_MINUTES
	);
	const [accountIds, setAccountIds] = useState<number[]>([]);

	useEffect(() => {
		const settle = window.setTimeout(
			() => setTimeoutMinutes(timeoutDraft),
			TIMEOUT_SETTLE_MS
		);
		return () => window.clearTimeout(settle);
	}, [timeoutDraft]);

	const params = useMemo(() => {
		const start = range[0].startOf('day');
		// The window is half-open, so the exclusive end is the next midnight.
		const end = range[1].add(1, 'day').startOf('day');
		return {
			start: start.toISOString(),
			end: end.toISOString(),
			timeoutMinutes,
			days: end.diff(start, 'day'),
		};
	}, [range, timeoutMinutes]);

	// Picking an account narrows the response enough that the server lifts its
	// window cap on the session list.
	const sessionListTooWide =
		!accountIds.length && params.days > MAX_SESSION_LIST_DAYS;

	const { data: summary, isFetching: summaryFetching } =
		useGetKittingSummaryQuery({
			start: params.start,
			end: params.end,
			timeoutMinutes: params.timeoutMinutes,
			userIds: accountIds,
			tz: BROWSER_TIMEZONE,
		});

	const { data: log, isFetching: logFetching } = useGetKittingLogQuery(
		{
			start: params.start,
			end: params.end,
			timeoutMinutes: params.timeoutMinutes,
			userIds: accountIds,
		},
		{ skip: sessionListTooWide }
	);

	const totals = summary?.totals;
	const roster = summary?.accounts;

	// The roster only covers the current window, so an account selected before
	// the range moved would fall out of it. Remembering the names already seen
	// keeps that selection readable instead of showing a bare user id.
	const [knownAccounts, setKnownAccounts] = useState<Record<number, string>>(
		{}
	);

	useEffect(() => {
		if (!roster?.length) return;
		setKnownAccounts((previous) => {
			const merged = { ...previous };
			let changed = false;
			roster.forEach(({ id, name }) => {
				if (merged[id] !== name) {
					merged[id] = name;
					changed = true;
				}
			});
			return changed ? merged : previous;
		});
	}, [roster]);

	const accountOptions = useMemo(() => {
		const options = new Map<number, string>();
		(roster ?? []).forEach(({ id, name }) => options.set(id, name));
		accountIds.forEach((id) => {
			if (!options.has(id)) {
				options.set(id, knownAccounts[id] ?? `Account ${id}`);
			}
		});
		return [...options.entries()]
			.map(([value, label]) => ({ value, label }))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [roster, accountIds, knownAccounts]);

	const attemptBreakdown: AttemptBreakdownRow[] = totals
		? [
				{
					key: 'first',
					attempt: 'First attempt',
					completed: totals.completed_first_attempt,
					timeouts: totals.timeouts_first_attempt,
					average: totals.average_duration_seconds_first_attempt,
				},
				{
					key: 'reprint',
					attempt: 'Reprint',
					completed: totals.completed_reprint,
					timeouts: totals.timeouts_reprint,
					average: totals.average_duration_seconds_reprint,
				},
			]
		: [];

	const dailyChartData = (summary?.daily ?? []).map((row) => ({
		date: dayjs(row.date).format('MMM D'),
		completed: row.completed,
		timeouts: row.timeouts,
	}));

	const columns: ColumnsType<KittingSessionRow> = [
		{
			title: 'Job',
			dataIndex: 'line_item',
			render: (label: string, row) => (
				<Link to={`/orders/line-items/${row.order_line_item_id}`}>
					{label || row.order_line_item_id}
				</Link>
			),
		},
		{
			title: (
				<>
					Printed from{' '}
					<Tooltip title={ACCOUNT_PROVENANCE_NOTE}>
						<InfoCircleOutlined />
					</Tooltip>
				</>
			),
			dataIndex: 'started_by',
			render: (startedBy: string, row) => {
				// Only worth naming the second account when a kit changed hands.
				const finishedBy =
					row.completed_by && row.completed_by !== startedBy
						? row.completed_by
						: null;
				if (!startedBy && !finishedBy) return <Text type="secondary">—</Text>;
				return (
					<>
						{startedBy || <Text type="secondary">Unattributed</Text>}
						{finishedBy && (
							<Text type="secondary" style={{ display: 'block' }}>
								finished from {finishedBy}
							</Text>
						)}
					</>
				);
			},
		},
		{
			title: 'First tag',
			dataIndex: 'started_at',
			render: (value: string) => formatTime(value),
			sorter: (a, b) => a.started_at.localeCompare(b.started_at),
			defaultSortOrder: 'descend',
		},
		{
			title: 'Final tag',
			dataIndex: 'completed_at',
			render: (value: string | null) => formatTime(value),
		},
		{
			title: 'Elapsed',
			dataIndex: 'elapsed_seconds',
			align: 'right',
			render: (value: number | null) => formatDuration(value),
			sorter: (a, b) => (a.elapsed_seconds ?? 0) - (b.elapsed_seconds ?? 0),
		},
		{
			title: 'Status',
			dataIndex: 'status',
			filters: (
				Object.keys(STATUS_LABELS) as KittingSessionStatus[]
			).map((status) => ({ text: STATUS_LABELS[status], value: status })),
			onFilter: (value, row) => row.status === value,
			render: (status: KittingSessionStatus) => (
				<Tag color={STATUS_COLORS[status]}>{STATUS_LABELS[status]}</Tag>
			),
		},
		{
			title: 'Attempt',
			dataIndex: 'is_reprint',
			filters: [
				{ text: 'First attempt', value: false },
				{ text: 'Reprint', value: true },
			],
			onFilter: (value, row) => row.is_reprint === value,
			render: (isReprint: boolean) =>
				isReprint ? <Tag>Reprint</Tag> : <Tag color="geekblue">First</Tag>,
		},
		{
			title: 'Tags printed',
			dataIndex: 'tag_count',
			align: 'right',
		},
	];

	return (
		<>
			<Header
				title="Kitting Times"
				breadcrumbItems={[
					{ title: <Link to="/">Home</Link> },
					{ title: 'Kitting Times' },
				]}
			/>

			<Space direction="vertical" size="large" style={{ width: '100%' }}>
				<Card className="shadow-sm">
					<Space wrap size="large" align="end">
						<div>
							<Text type="secondary" style={{ display: 'block' }}>
								Date range
							</Text>
							<RangePicker
								value={range}
								allowClear={false}
								onChange={(dates) => {
									if (dates && dates[0] && dates[1]) {
										setRange([dates[0], dates[1]]);
									}
								}}
								disabledDate={(current) => current.isAfter(dayjs(), 'day')}
							/>
						</div>
						<div>
							<Text type="secondary" style={{ display: 'block' }}>
								Printed from{' '}
								<Tooltip
									title={`${ACCOUNT_PROVENANCE_NOTE} Matches the account on either the first or the final tag, so a kit that changed hands shows up for both.`}
								>
									<InfoCircleOutlined />
								</Tooltip>
							</Text>
							<Select
								mode="multiple"
								allowClear
								placeholder="All stations"
								style={{ width: ACCOUNT_SELECT_WIDTH }}
								loading={summaryFetching}
								value={accountIds}
								options={accountOptions}
								optionFilterProp="label"
								onChange={setAccountIds}
								notFoundContent={
									summaryFetching ? null : 'Nothing was kitted in this range'
								}
							/>
						</div>
						<div>
							<Text type="secondary" style={{ display: 'block' }}>
								Timeout threshold{' '}
								<Tooltip
									title={
										'A kit that takes longer than this is treated as bad data: ' +
										'the operator most likely printed the tags and got pulled ' +
										'away. Those sessions are still counted, but they are left ' +
										'out of every average.'
									}
								>
									<InfoCircleOutlined />
								</Tooltip>
							</Text>
							<InputNumber
								min={1}
								max={24 * 60}
								step={5}
								value={timeoutDraft}
								addonAfter="min"
								onChange={(value) =>
									setTimeoutDraft(value ?? DEFAULT_TIMEOUT_MINUTES)
								}
							/>
						</div>
					</Space>
				</Card>

				<Row gutter={[16, 16]}>
					<Col xs={12} md={6}>
						<Card className="shadow-sm" loading={summaryFetching}>
							<Statistic title="Sessions" value={totals?.sessions ?? 0} />
						</Card>
					</Col>
					<Col xs={12} md={6}>
						<Card className="shadow-sm" loading={summaryFetching}>
							<Statistic
								title="Average duration"
								value={formatDuration(totals?.average_duration_seconds)}
							/>
						</Card>
					</Col>
					<Col xs={12} md={6}>
						<Card className="shadow-sm" loading={summaryFetching}>
							<Statistic
								title="Completed"
								value={totals?.completed ?? 0}
								valueStyle={{ color: '#3f8600' }}
							/>
						</Card>
					</Col>
					<Col xs={12} md={6}>
						<Card className="shadow-sm" loading={summaryFetching}>
							<Statistic
								title="Timed out"
								value={totals?.timeouts ?? 0}
								valueStyle={{ color: '#cf1322' }}
							/>
						</Card>
					</Col>
				</Row>

				<Row gutter={[16, 16]}>
					<Col xs={24} lg={10}>
						<Card
							className="shadow-sm"
							title="First attempts vs reprints"
							loading={summaryFetching}
						>
							<Table<AttemptBreakdownRow>
								size="small"
								pagination={false}
								rowKey="key"
								dataSource={attemptBreakdown}
								columns={[
									{ title: '', dataIndex: 'attempt' },
									{
										title: 'Completed',
										dataIndex: 'completed',
										align: 'right',
									},
									{
										title: 'Timed out',
										dataIndex: 'timeouts',
										align: 'right',
									},
									{
										title: 'Average',
										dataIndex: 'average',
										align: 'right',
										render: (value: number | null) => formatDuration(value),
									},
								]}
							/>
							<Text type="secondary" style={{ marginTop: 12, display: 'block' }}>
								{totals?.in_progress ?? 0} session(s) still open within the
								threshold, across {totals?.job_count ?? 0} job(s).
							</Text>
						</Card>
					</Col>
					<Col xs={24} lg={14}>
						<Card
							className="shadow-sm"
							title="Sessions per day"
							loading={summaryFetching}
						>
							{dailyChartData.length ? (
								<ResponsiveContainer width="100%" height={240}>
									<BarChart data={dailyChartData}>
										<CartesianGrid strokeDasharray="3 3" vertical={false} />
										<XAxis dataKey="date" />
										<YAxis allowDecimals={false} />
										<ChartTooltip />
										<Legend />
										<Bar
											dataKey="completed"
											name="Completed"
											stackId="sessions"
											fill="#52c41a"
										/>
										<Bar
											dataKey="timeouts"
											name="Timed out"
											stackId="sessions"
											fill="#ff4d4f"
										/>
									</BarChart>
								</ResponsiveContainer>
							) : (
								<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
							)}
						</Card>
					</Col>
				</Row>

				<Card className="shadow-sm" title="Kitting sessions">
					{sessionListTooWide ? (
						<Alert
							type="info"
							showIcon
							message={`Narrow the range to ${MAX_SESSION_LIST_DAYS} days or fewer, or pick a station, to list individual sessions.`}
							description="The totals above still cover the full range."
						/>
					) : (
						<Table<KittingSessionRow>
							rowKey="id"
							size="small"
							loading={logFetching}
							dataSource={log?.results ?? []}
							columns={columns}
							pagination={{ pageSize: 25, showSizeChanger: true }}
						/>
					)}
				</Card>
			</Space>
		</>
	);
};

export default KittingTimes;
